import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSlider from '../components/HeroSlider';
import ProductCard from '../components/ProductCard';
import CountdownTimer from '../components/CountdownTimer';
import PriceDisplay from '../components/PriceDisplay';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { productsApi, categoriesApi } from '../services/api';
import { useCart } from '../context/CartContext';

const HomePage = () => {
  const { addToCart, getCartCount } = useCart();
  const [bestsellers, setBestsellers] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [weeklyDeal, setWeeklyDeal] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bestsellersData, newData, featuredData, categoriesData, allProducts] = await Promise.all([
        productsApi.getBestsellers(),
        productsApi.getNew(),
        productsApi.getFeatured(),
        categoriesApi.getAll(),
        productsApi.getAll(), // Fetch all products to calculate category stats
      ]);

      setBestsellers(bestsellersData.slice(0, 4));
      setNewProducts(newData.slice(0, 8));
      setFeaturedProducts(featuredData);
      
      // Backend returns only parent categories with nested .subcategories.
      // Flatten so that sub-categories can also be shown in the homepage
      // "Koleksiyonlar" block if admin enabled them.
      const flatCategories = [];
      (categoriesData || []).forEach(root => {
        flatCategories.push(root);
        (root.subcategories || []).forEach(sub => {
          flatCategories.push(sub);
        });
      });
      
      // Enhance categories with product data
      const enhancedCategories = flatCategories
        .filter(category => category.enabled !== false && category.show_in_collections) // aktif + koleksiyonda göster
        .map(category => {
        // Get products for this category
        const categoryProducts = allProducts.filter(p => p.category === category.slug);
        
        if (categoryProducts.length > 0) {
          // Find most expensive product for default image
          const mostExpensive = categoryProducts.reduce((max, p) => 
            p.price > max.price ? p : max
          );
          
          // Find cheapest product for starting price
          const cheapest = categoryProducts.reduce((min, p) => 
            p.price < min.price ? p : min
          );
          
          // If admin picked a specific cover image, use it; else fallback to most expensive
          const coverImage = category.collection_image || mostExpensive.image;
          
          return {
            ...category,
            image: coverImage,
            starting_price: cheapest.price
          };
        }
        
        // No products but admin may have set cover image
        if (category.collection_image) {
          return { ...category, image: category.collection_image };
        }
        
        return category;
      });
      
      setCategories(enhancedCategories);

      // Get weekly deal
      try {
        const weeklyDealData = await productsApi.getWeeklyDeal();
        setWeeklyDeal(weeklyDealData);
      } catch (error) {
        console.log('No weekly deal available');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    alert(`${product.name} sepete eklendi!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO />
      <Header cartCount={getCartCount()} />

      {/* Hero Slider */}
      <HeroSlider />

      {/* Bestsellers Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Çok Satanlar</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bestsellers.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Deal Section */}
      {weeklyDeal && (
        <section className="py-12 bg-gradient-to-br from-red-50 to-orange-100">
          <div className="container mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Product Image */}
                <div className="p-8 flex items-center justify-center bg-gray-50">
                  <img
                    src={weeklyDeal.image}
                    alt={weeklyDeal.name}
                    className="max-w-full h-auto max-h-96 object-contain border-2 border-gray-300 shadow-sm"
                  />
                </div>

                {/* Product Info */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="inline-block bg-orange-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 w-fit">
                    Haftanın Fırsatı
                  </div>
                  
                  <CountdownTimer targetDate={new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)} />

                  <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mt-8 mb-4">
                    Fırsat Kapında - Bu Şansı Kaçırma!
                  </h3>
                  
                  <h4 className="text-xl font-semibold text-gray-700 mb-4">
                    {weeklyDeal.name}
                  </h4>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Telefon ve Akıllı Saat İçin Çok Fonksiyonlu Güç
                  </p>

                  <div className="mb-6">
                    <PriceDisplay 
                      price={weeklyDeal.price}
                      oldPrice={weeklyDeal.old_price}
                      discount={weeklyDeal.discount}
                      size="large"
                    />
                  </div>

                  <Link
                    to={`/product/${weeklyDeal.id}`}
                    className="inline-block bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-all duration-300 font-semibold text-center shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Fırsatı Yakala
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* New Products Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Yeni Gelen Ürünler</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.slice(0, 3).map((product, index) => (
        <section
          key={product.id}
          className={`py-12 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
        >
          <div className="container mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className={`p-8 ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-auto rounded-lg border-2 border-gray-300 shadow-sm"
                  />
                </div>
                <div className={`p-8 flex flex-col justify-center ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">
                    {product.name}
                  </h3>
                  <div className="mb-6">
                    <PriceDisplay 
                      price={product.price}
                      oldPrice={product.old_price}
                      discount={product.discount}
                      size="large"
                    />
                  </div>
                  <Link
                    to={`/product/${product.id}`}
                    className="inline-block bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-all duration-300 font-medium text-center w-fit"
                  >
                    Hemen Satın Al
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Collections Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Koleksiyonlar</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="aspect-square bg-gray-50 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 border-2 border-gray-300 shadow-sm"
                  />
                </div>
                <div className="p-4 text-center">
                  <h3 className="font-semibold text-gray-800 mb-2 group-hover:text-red-600 transition-colors">
                    {category.name}
                  </h3>
                  {category.starting_price && (
                    <p className="text-sm text-gray-600">
                      Başlangıç fiyatı <span className="font-bold text-red-600">{category.starting_price.toLocaleString('tr-TR')} TL</span>
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
